import React from 'react';
import { useTranslation } from 'react-i18next';
import InfoText from '@/components/atoms/InfoText';
import ExpandableText from '@/components/atoms/ExpandableText';
import StarRating from '@/components/atoms/StarRating';
import { ProductFeedbackItem } from '@/types/logisticsFeedback';
import { Image } from 'antd';
import { FeedbackProductRow, ProductRowPart } from './FeedbackProductRow';
import { formatFeedbackDate } from '../utils/formatFeedbackDate';

interface FeedbackProductsListProps {
  products: ProductFeedbackItem[];
  productsCount?: number;
  'data-testid'?: string;
}

const PRODUCT_ROW_TEST_IDS: Record<ProductRowPart, string> = {
  header: 'product-preview-header',
  image: 'product-preview-image',
  name: 'product-preview-name',
  attributes: 'product-preview-attributes',
  qtyCol: 'product-preview-qty-col',
  qtyLabel: 'product-preview-qty-label',
  qtyValue: 'product-preview-qty-value',
  priceCol: 'product-preview-price-col',
  priceLabel: 'product-preview-price-label',
  priceWrapper: 'product-preview-price-wrapper',
  priceValue: 'product-preview-price-value',
  currency: 'product-preview-currency',
  toggle: 'product-preview-toggle',
  customization: 'product-preview-customization',
};

export const FeedbackProductsList: React.FC<FeedbackProductsListProps> = ({
  products,
  productsCount = products.length,
  'data-testid': dataTestId = 'store-all-products',
}) => {
  const { t, i18n } = useTranslation();

  return (
    <div
      className="space-y-6 p-6 border rounded-2xl bg-light-surface-primary dark:bg-dark-surface-primary border-light-input-border dark:border-dark-input-border"
      data-testid={dataTestId}
    >
      {/* Header matching AllProducts.tsx */}
      <div className="flex items-center justify-between" data-testid={`${dataTestId}-header`}>
        <div>
          <InfoText
            text={t('logisticsFeedback.allProducts')}
            className="!font-bold text-xl !text-light-primary dark:!text-dark-primary"
            data-testid={`${dataTestId}-title`}
          />
          <InfoText
            text={t('logisticsFeedback.allProductsDesc')}
            className="!text-text-secondary dark:!text-dark-text-secondary !text-sm mt-0.5"
            data-testid={`${dataTestId}-desc`}
          />
        </div>

        <div className="text-start flex flex-col justify-center gap-2.5" data-testid={`${dataTestId}-count-wrapper`}>
          <InfoText
            text={`${productsCount} ${t('logisticsFeedback.productUnit')}`}
            className="!font-bold !text-sm !leading-[100%] !text-light-information dark:!text-dark-information"
            data-testid={`${dataTestId}-count`}
          />
          <InfoText
            text={t('logisticsFeedback.totalInOrder')}
            className="!font-normal !text-sm !leading-[100%] !text-light-information dark:!text-dark-information"
            data-testid={`${dataTestId}-total-in-order`}
          />
        </div>
      </div>

      {/* Products list matching ProductPreview & ReviewCard */}
      <div className="space-y-5 my-4" data-testid={`${dataTestId}-items`}>
        {products.map((product, pIdx) => (
          <React.Fragment key={product.id}>
            <div
              className="space-y-3"
              data-testid={`store-order-details-product-preview-${pIdx}`}
            >
              <FeedbackProductRow
                product={product}
                getTestId={(part) => `${PRODUCT_ROW_TEST_IDS[part]}-${pIdx}`}
              />

              {/* Review is always visible, independent of the dropdown */}
              {product.review && (
                <div
                  className="flex flex-col gap-2.5 pt-2"
                  data-testid={`review-card-${pIdx}`}
                >
                  <InfoText
                    text={t('logisticsFeedback.review')}
                    className="!text-base !font-bold !text-light-primary dark:!text-dark-primary"
                    data-testid={`review-card-header-text-${pIdx}`}
                  />

                  {/* Submitted at date & Rating row */}
                  <div className="flex items-center justify-between">
                    <InfoText
                      text={`${t('logisticsFeedback.submittedAt')} ${formatFeedbackDate(product.review.date, i18n.language)}`}
                      className="!text-sm !font-normal !text-text-secondary dark:!text-dark-text-secondary"
                      data-testid={`review-card-date-${pIdx}`}
                    />

                    <div
                      className="flex items-center gap-1.5"
                      data-testid={`review-card-rating-${pIdx}`}
                    >
                      <InfoText
                        text={`(${product.review.rating.toFixed(1)})`}
                        className="!text-sm !font-bold !text-light-primary dark:!text-dark-primary"
                      />
                      <StarRating
                        value={product.review.rating}
                        data-testid={`review-card-stars-${pIdx}`}
                      />
                    </div>
                  </div>

                  {product.review.reason && (
                    <div data-testid={`review-card-reason-${pIdx}`}>
                      <InfoText
                        text={product.review.reason}
                        className="!text-sm !font-semibold !text-light-primary dark:!text-dark-primary"
                      />
                    </div>
                  )}

                  {product.review.attachments && product.review.attachments.length > 0 && (
                    <div
                      className="flex flex-row flex-wrap gap-3"
                      data-testid={`review-attachments-${pIdx}`}
                    >
                      {product.review.attachments.map((imgUrl) => (
                        <Image
                          key={imgUrl}
                          src={imgUrl}
                          width={56}
                          height={56}
                          alt={product.name}
                          className="rounded-[12px] object-cover border border-light-input-border dark:border-dark-input-border"
                        />
                      ))}
                    </div>
                  )}

                  {product.review.comment && (
                    <div data-testid={`review-card-desc-${pIdx}`}>
                      <ExpandableText
                        text={product.review.comment}
                        limit={140}
                        className="!text-sm !font-normal !leading-relaxed !text-text-secondary dark:!text-dark-text-secondary"
                      />
                    </div>
                  )}
                </div>
              )}
            </div>

            {pIdx < products.length - 1 && (
              <hr
                className="border-light-input-border dark:border-dark-input-border"
                key={`separator-${product.id}`}
              />
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};
